<template>
  <component
    :is="tag"
    :class="[
      // Base
      'v-card',
      'd-flex',

      // Background variant
      bgVariantOutput.class && `bg-${bgVariantOutput.class}`,

      // Overlay
      {
        'v-card--overlay': overlay
      },

      // Image position
      {
        // Small breakpoint
        'flex-column': !imgPosition || imgPosition === 'top',
        'flex-row': imgPosition === 'left',
        'flex-row-reverse': imgPosition === 'right',

        // Medium breakpoint
        'flex-md-column': imgMdPosition === 'top',
        'flex-md-row': imgMdPosition === 'left',
        'flex-md-row-reverse': imgMdPosition === 'right',

        // Large breakpoint
        'flex-lg-column': imgLgPosition === 'top',
        'flex-lg-row': imgLgPosition === 'left',
        'flex-lg-row-reverse': imgLgPosition === 'right'
      }
    ]"
    :style="{
      backgroundColor: bgVariantOutput.color
    }"
  >
    <!-- Overlay -->
    <div
      v-if="overlay"
      :class="[
        'v-card__overlay'
      ]"
      :style="{
        backgroundImage: !!imgSrc && `url('${imgSrc}')`,
        backgroundPosition: !!imgAlign && imgAlign
      }"
    >
      <div
        v-if="!!imgSrc"
        :class="[
          'v-card__overlay__color-1',
          bgVariantOutput.class && `text-${bgVariantOutput.class}`
        ]"
        :style="{
          color: bgVariantOutput.color,
          backgroundImage: !!imgSrc && `linear-gradient(90deg, currentColor, currentColor 5%, rgba(0,0,0,0))`,
          backgroundPosition: !!imgAlign && imgAlign
        }"
      >
      </div>
      <div
        v-if="!!imgSrc"
        :class="[
          'v-card__overlay__color-2',
          bgVariantOutput.class && `text-${bgVariantOutput.class}`
        ]"
        :style="{
          color: bgVariantOutput.color,
          backgroundImage: !!imgSrc && `linear-gradient(-180deg, currentColor, currentColor 5%, rgba(0,0,0,0))`,
          backgroundPosition: !!imgAlign && imgAlign
        }"
      >
      </div>
    </div>

    <!-- Image container -->
    <div
      v-if="!!imgSrc"
      :class="['v-card__image']"
      :style="{
        backgroundImage: !overlay && !!imgSrc && `url('${imgSrc}')`,
        backgroundPosition: !!imgAlign && imgAlign,
      }"
    >
    </div>

    <!-- Card content -->
    <div :class="['v-card__content']">
      <div :class="['p-4', { 'py-5': !!imgSrc }]">
        <slot></slot>
      </div>
    </div>
  </component>
</template>

<script>
export default {
  props: {
    tag: {
      type: String,
      default: "div"
    },
    bgVariant: {
      type: String,
      default: "light"
    },
    imgSrc: {
      type: String
    },
    imgPosition: {
      type: String,
      default: "top"
    },
    imgMdPosition: {
      type: String
    },
    imgLgPosition: {
      type: String
    },
    imgAlign: {
      type: String
    },
    overlay: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    bgVariantOutput: function() {
      const variant = this.$props.bgVariant;

      if (!variant) {
        return "light";
      }

      const isHex = /^#[a-zA-Z0-9]{6}|#[a-zA-Z0-9]{3}$/.test(variant);

      return {
        class: !isHex && `${variant}`,
        color: isHex && variant
      };
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../sass/component.scss";

.v-card {
  position: relative;
  z-index: 1;
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: $border-radius;
  overflow: hidden;

  &__overlay {
    position: absolute;
    overflow: hidden;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    right: 0;
    top: 0;
    width: 75%;
    height: 100%;
    z-index: 1;

    .v-card.flex-column & {
      right: auto;
      top: 0;
      left: 0;
      width: 100%;
      height: 80%;
    }

    .v-card.flex-row & {
      right: auto;
      top: 0;
      left: 0;
      width: 75%;
      height: 100%;
    }

    .v-card.flex-row-reverse & {
      right: 20;
      top: 0;
      left: auto;
      width: 75%;
      height: 100%;
    }

    .v-card.flex-md-column & {
      @include media-breakpoint-up(md) {
        right: auto;
        top: 0;
        left: 0;
        width: 100%;
        height: 80%;
      }
    }

    .v-card.flex-md-row & {
      @include media-breakpoint-up(md) {
        right: auto;
        top: 0;
        left: 0;
        width: 75%;
        height: 100%;
      }
    }

    .v-card.flex-md-row-reverse & {
      @include media-breakpoint-up(md) {
        right: 20;
        top: 0;
        left: auto;
        width: 75%;
        height: 100%;
      }
    }

    .v-card.flex-lg-column & {
      @include media-breakpoint-up(lg) {
        right: auto;
        top: 0;
        left: 0;
        width: 100%;
        height: 80%;
      }
    }

    .v-card.flex-lg-row & {
      @include media-breakpoint-up(lg) {
        right: auto;
        top: 0;
        left: 0;
        width: 75%;
        height: 100%;
      }
    }

    .v-card.flex-lg-row-reverse & {
      @include media-breakpoint-up(lg) {
        right: 20;
        top: 0;
        left: auto;
        width: 75%;
        height: 100%;
      }
    }

    &__color {
      &-1,
      &-2 {
        position: absolute;
        top: -1%;
        left: -1%;
        width: 102%;
        height: 102%;
        display: none;
      }

      .v-card.flex-column &-2 {
        display: block;
        transform: rotate(180deg) translateY(-1px);
      }

      .v-card.flex-row &-1 {
        display: block;
        transform: rotate(180deg) translateX(-1px);
      }

      .v-card.flex-row-reverse &-1 {
        display: block;
        transform: translateX(-1px);
      }

      @include media-breakpoint-up(md) {
        .v-card.flex-md-column &-1 {
          display: none;
        }
        .v-card.flex-md-column &-2 {
          display: block;
          transform: rotate(180deg) translateY(-1px);
        }
      }

      @include media-breakpoint-up(md) {
        .v-card.flex-md-row &-2 {
          display: none;
        }
        .v-card.flex-md-row &-1 {
          display: block;
          transform: rotate(180deg) translateX(-1px);
        }
      }

      @include media-breakpoint-up(md) {
        .v-card.flex-md-row-reverse &-2 {
          display: none;
        }
        .v-card.flex-md-row-reverse &-1 {
          display: block;
          transform: translateX(-1px);
        }
      }

      @include media-breakpoint-up(lg) {
        .v-card.flex-lg-column &-1 {
          display: none;
        }
        .v-card.flex-lg-column &-2 {
          display: block;
          transform: rotate(180deg) translateY(-1px);
        }
      }

      @include media-breakpoint-up(lg) {
        .v-card.flex-lg-row &-2 {
          display: none;
        }
        .v-card.flex-lg-row &-1 {
          display: block;
          transform: rotate(180deg) translateX(-1px);
        }
      }

      @include media-breakpoint-up(lg) {
        .v-card.flex-lg-row-reverse &-2 {
          display: none;
        }
        .v-card.flex-lg-row-reverse &-1 {
          display: block;
          transform: translateX(-1px);
        }
      }
    }
  }

  &__content {
    flex: 1 1 0;
    z-index: 2;
  }

  &__image {
    flex: 1 1 0;
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;

    &::before {
      content: "";
      display: block;
      padding-top: 75%;
    }
  }
}
</style>
